import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Col, Modal, Row } from "react-bootstrap";
import { Keypair, PublicKey, Transaction } from "@solana/web3.js";
import { BN } from "@coral-xyz/anchor";
import * as token from "@solana/spl-token";

import { useConnection, useWallet } from "@solana/wallet-adapter-react";
import { useDarknetIdentity } from "../../contexts/IdentityContext";
import { Blockchain } from "../../shared/enums";
import ApiService from "../../services/ApiService";
import StorageService from "../../services/StorageService";
import { formatNumber, getGameAssetsUrl } from "../../utilities/Helpers";
import { getTokenIcons } from "../../utilities/Helpers";
import { TokenInfo } from "@solana/spl-token-registry";

const DEFAULT_ADMIN = process.env.REACT_APP_DEFAULT_ADMIN ?? "";

const WageredModal = () => {
  const txFee: number = 0.05;
  const navigate = useNavigate();
  const { connection } = useConnection();
  const { publicKey, connected } = useWallet();
  const wallet = useWallet();
  const { authenticated, program } = useDarknetIdentity();
  const [show, setShow] = useState(false);
  const [blockchain, setBlockchain] = useState<Blockchain>(Blockchain.Solana);
  const [walletBalance, setWalletBalance] = useState<number>(0);
  const [wagerAmount, setWagerAmount] = useState<number>(0.03);
  const [minWager, setMinWager] = useState<number>(0.03);

  const [tokens, setTokens] = useState<Token[]>([]);
  const [tokenIcons, setTokenIcons] = useState<Map<string, TokenInfo>>();
  const [tokenSelected, setTokenSelected] = useState<Token>();

  useEffect(() => {
    if (publicKey && connected) {
      connection.getBalance(publicKey).then((balance) => {
        setWalletBalance(balance / 1e9);
      }).catch((error) => {
        console.log(error);
      });
    }
  }, [connected, connection, publicKey]);

  useEffect(() => {
    ApiService.get("/tokens").then((response) => {
      if (!response) throw new Error("No response");
      if (response.success) {
        setTokens(response.data);
        getTokenIcons(response.data).then((icons) => setTokenIcons(icons)).catch((error) => console.log(error));
      }
    }).catch((error) => {
      console.log(error);
    });
  }, []);

  async function handleCreateWageredGame() {
    if (!authenticated) return;
    if (!program) return;
    if (!tokenSelected || wagerAmount > walletBalance) return;
    console.log("creating wagered game");
    console.log("program", program.programId.toString());
    console.log("wagerAmount", wagerAmount);

    // Darknet default admin
    const admin = new PublicKey(DEFAULT_ADMIN);
    const wager = Keypair.generate();

    const [wagerVault] = PublicKey.findProgramAddressSync(
      [wager.publicKey.toBuffer()],
      program.programId
    );

    const [player] = PublicKey.findProgramAddressSync(
      [wager.publicKey.toBuffer(), publicKey!.toBuffer()],
      program.programId
    );

    try {
      console.log("selectedToken", tokenSelected);
      console.log("admin", admin);
      console.log("wager.publicKey", wager.publicKey);
      console.log("wagerVault", wagerVault);
      console.log("publicKey", publicKey);
      console.log("program", program);
      // wager options = spl: bool, buy_in: u64, max_players: u8, text: String,
      const createWagerTx = new Transaction();
      if (tokenSelected?.symbol === "SOL") {
        const createWagerIx = await program.methods
          .createWager(false, new BN(wagerAmount * 1e9), 2, "sol pong game")
          .accounts({
            admin: admin,
            wager: wager.publicKey,
            wagerVault: wagerVault,
            player: player,
            authority: publicKey!,
            // @ts-ignore
            token: null,
            // @ts-ignore
            authorityTokenAccount: null,
            // @ts-ignore
            wagerVaultTokenAccount: null,
            // @ts-ignore
            tokenProgram: null,
            // @ts-ignore
            associatedTokenProgram: null,
          })
          .signers([wager])
          .instruction();
        createWagerTx.add(createWagerIx);
      } else {
        const tokenMint = new PublicKey(tokenSelected!.address);
        const authorityTokenAccount = await token.getAssociatedTokenAddress(
          tokenMint,
          publicKey!,
          true
        );
        const wagerVaultTokenAccount = await token.getAssociatedTokenAddress(
          tokenMint,
          wagerVault,
          true
        );
        const createWagerIx = await program.methods
          .createWager(
            true,
            new BN(wagerAmount * Math.pow(10, tokenSelected?.decimals!)),
            2,
            "spl pong game"
          )
          .accounts({
            admin: admin,
            wager: wager.publicKey,
            wagerVault: wagerVault,
            player: player,
            authority: publicKey!,
            // @ts-ignore
            token: tokenMint,
            // @ts-ignore
            authorityTokenAccount: authorityTokenAccount,
            // @ts-ignore
            wagerVaultTokenAccount: wagerVaultTokenAccount,
            // @ts-ignore
            tokenProgram: token.TOKEN_PROGRAM_ID,
            // @ts-ignore
            associatedTokenProgram: token.ASSOCIATED_TOKEN_PROGRAM_ID,
          })
          .signers([wager])
          .instruction();
        createWagerTx.add(createWagerIx);
      }
      createWagerTx.feePayer = publicKey!;

      const latestBlockHash =
        await program.provider.connection.getLatestBlockhash();
      createWagerTx.recentBlockhash = latestBlockHash.blockhash;
      createWagerTx.partialSign(wager);
      const sig = await wallet.sendTransaction(
        createWagerTx,
        program.provider.connection
      );
      const tx = await program.provider.connection.confirmTransaction({
        blockhash: latestBlockHash.blockhash,
        lastValidBlockHeight: latestBlockHash.lastValidBlockHeight,
        signature: sig,
      });
      console.log("confirmed tx", tx);

      if (!tx.value.err) {
        let response = await ApiService.post("/creategame", {
          wagered: true,
          wager: wagerAmount,
          blockchain: blockchain,
          wagerAccount: wager.publicKey.toString(),
          tokenId: tokenSelected?.id,
        });

        if (response && response.success) {
          StorageService.set("authToken", response.data.token);
          navigate(`/pong/${response.data.id}`);
        } else {
          console.error(response.message);
          window.alert(response.message);
        }
      } else {
        console.log("error", tx.value.err);
        window.alert(tx.value.err);
      }
    } catch (error) {
      console.log(error);
      window.alert(error);
    }
  }

  const handleCryptoSelected = (crypto: Blockchain) => {
    setBlockchain(crypto);
    setWagerAmount(0);
  };

  const handleSetWagerAmount = (event: any) => {
    let wager = Number(event.currentTarget.value);
    if (wager < minWager) wager = minWager;
    if (wager > walletBalance) wager = walletBalance;
    setWagerAmount(wager);
  };

  const validateWagerAmount = (event: any) => {
    let wager = Number(event.currentTarget.value);
    if (wager < minWager) wager = minWager;
    if (wager > walletBalance) wager = walletBalance;
    event.currentTarget.value = wager;
    setWagerAmount(wager);
  };

  const handleTokenSelected = async (event: any) => {
    let selectedToken = tokens.find(
      (token) => token.address === event.currentTarget.value
    );

    if (!selectedToken) return;

    setTokenSelected(selectedToken);
    let options = selectedToken?.options ?? [];
    let min = Number(options[options.length - 1] ?? 0);
    setMinWager(min);
    setWagerAmount(min);

    try {
      if (selectedToken.symbol === "SOL") {
        if (!publicKey) throw new Error("No wallet connected");

        let balance = await connection.getBalance(publicKey);
        setWalletBalance(balance / 1e9);
      } else {
        let tokenMint = new PublicKey(selectedToken.address);
        let address = await token.getAssociatedTokenAddress(tokenMint, publicKey!)
        let decimals = selectedToken?.decimals ?? 0;
        let details = await token.getAccount(connection, address);
        let balance = Number(details.amount) / Math.pow(10, decimals);
        setWalletBalance(balance);
      }
    } catch (error: any) {
      console.error(error);
      setWalletBalance(0);
    }
  };

  const FeaturedGames = () => {
    return (<>
      <h6>Available Games</h6>
      <Row className="g-0 pt-1">
        <Col xs={2} className="text-center">
          <button className="dg-btn dg-btn-mid-dark py-2 px-1 text-center w-75">
            <img src={`${getGameAssetsUrl(1)}/icon.png`} alt="Dark Pong" height={32} />&nbsp;Dark Pong
          </button>
          {/* 
          <CustomTooltip title="Dark Pong" id="dark-pong-wagered" placement={"bottom"}>
            <button className="dg-btn dg-btn-mid-dark py-2">
              <img src={`${getGameAssetsUrl(1)}/icon.png`} alt="Dark Pong" height={32} />
            </button>
          </CustomTooltip>
            */}
        </Col>
      </Row>
    </>);
  }

  const Tokens = () => {
    //<TokenTooltip token={token} />
    return (
      <>
        <h6>Choose a token</h6>
        <Row className="g-3 pt-1">
          {tokens.map((token) => {
            let icon = tokenIcons?.get(token.address)?.logoURI ?? tokenIcons?.get(token.testnet)?.logoURI;
            if (!icon || icon.length === 0) icon = `https://static.darknetgames.xyz/tokens/${token.address}.jpg`;
            return (
              <Col key={token.symbol} xs={2} className="text-center">
                <button
                  className="dg-btn dg-btn-mid-dark py-2 px-1 text-center w-100"
                  onClick={handleTokenSelected}
                  value={token.address}
                >
                  <img src={icon} height={32} alt={token?.symbol} />
                  <br />
                  {token?.name}
                </button>
              </Col>
            )
          })}
        </Row>
      </>
    );
  };

  const PreselectedOptions = () => {
    if (!tokenSelected || !tokenSelected.options) return <></>;
    return (
      <>
        <span>Decide your wager</span>
        <Row className="g-3 mt-0">
          {tokenSelected.options.map((option) => {
            let index = tokenSelected.options?.indexOf(option);
            let optionId = `option_${tokenSelected.symbol}_${index}`;
            if (index === 5) {
              return (
                <div
                  className="bg-light-grey text-light d-block"
                  key={optionId}
                >
                  <div className="dg-btn dg-btn-mid-dark dg-btn-block px-auto">
                    <input
                      type="number"
                      className="bg-transparent p-0 w-55 text-end text-light b-0"
                      title="wager amount"
                      min={minWager}
                      step={step()}
                      max={walletBalance}
                      defaultValue={wagerAmount}
                      onBlur={validateWagerAmount}
                    />{" "}
                    {tokenSelected?.symbol}
                  </div>
                </div>
              );
            }
            return (
              <Col key={optionId}>
                <button
                  className="dg-btn dg-btn-mid-dark dg-btn-block"
                  onClick={handleSetWagerAmount}
                  value={option}
                >
                  {formatNumber(option)} {tokenSelected.symbol}
                </button>
              </Col>
            );
          })}
        </Row>
        <hr />
      </>
    );
  };

  const step = () => {
    if (!tokenSelected) return 1;

    if (tokenSelected.symbol === "SOL" || tokenSelected.symbol === "USDC")
      return 0.01;

    return Math.pow(10, tokenSelected.decimals);
  }

  return (
    <>
      <button
        className="dg-btn dg-btn-primary text-light px-3 pt-1 pb-2 fs-7"
        onClick={() => setShow(true)}
      >
        Create Wagered Game
      </button>

      <Modal
        size="lg"
        show={show}
        onHide={() => setShow(false)}
        className="dg-modal"
      >
        <Modal.Header closeButton closeVariant="white">
          <Modal.Title>
            Create Wagered Game
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row className="text-center d-none">
            <Col>
              <button
                className="dg-btn dg-btn-mid-dark dg-btn-block"
                onClick={() => handleCryptoSelected(Blockchain.Solana)}
              >
                Solana
              </button>
            </Col>
            <Col>
              <button
                className="dg-btn dg-btn-dark dg-btn-block"
                onClick={() => handleCryptoSelected(Blockchain.Ethereum)}
                disabled={true}
              >
                Ethereum
              </button>
            </Col>
          </Row>
          <Row className="g-3 mb-3">
            <FeaturedGames />
          </Row>
          <hr />
          <Row className="g-3 mb-3">
            <Tokens />
          </Row>
          <hr />
          <Row className="g-3">
            <PreselectedOptions />
          </Row>
          {tokenSelected && (<>
            <Row className="text-center fs-3 justify-content-center">
              <h6 className="text-start">Confirm the price</h6>
              <Col xs={5}>
                {((wagerAmount > walletBalance || wagerAmount < minWager) && (
                  <span className="text-danger">
                    {formatNumber(wagerAmount)} {tokenSelected?.symbol}
                  </span>
                )) || (
                    <span>
                      {formatNumber(wagerAmount)} {tokenSelected?.symbol}
                    </span>
                  )}
                <br />
                <span className="fs-8">Current Wager</span>
              </Col>
              <Col xs={1} className="fs-0">
                /
              </Col>
              <Col xs={5}>
                {formatNumber(Number(walletBalance.toFixed(3)))} {tokenSelected?.symbol}
                <br />
                <span className="fs-8">Wallet Balance</span>
              </Col>
            </Row>
            <hr />
          </>
          )}
          <Row className="fs-8 mt-3 text-grey text-justify">
            <h6 className="text-start">Disclaimer</h6>
            <Col>
              Darknet Games is a web3 gaming platform intended for users aged 18 and over.
              Please be aware that a <span className="text-success">{txFee * 100}% fee</span> will
              be imposed on the winnings of each game played on the platform, and this fee
              is used for game development and future projects. The minimum amount that can
              be wagered on the platform is <span className="text-success">{formatNumber(minWager)}{" "}
                {tokenSelected?.symbol}</span>. Please note that users should always
              account for gas costs when making transactions on the platform. By
              using Darknet Games, you acknowledge and agree to these terms.
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer className="">
          <Row className="justify-content-end">
            <Col>
              <button
                className="dg-btn dg-btn-secondary dg-btn-block"
                onClick={() => setShow(false)}
              >
                Close
              </button>
            </Col>
            <Col>
              <button
                className={`dg-btn dg-btn-block text-light ${!tokenSelected || wagerAmount > walletBalance || wagerAmount < minWager ? "dg-btn-disabled" : "dg-btn-primary"}`}
                onClick={handleCreateWageredGame}
                disabled={!tokenSelected || wagerAmount > walletBalance || wagerAmount < minWager}
              >
                Create&nbsp;Game
              </button>
            </Col>
          </Row>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default WageredModal;
